/** @file assignMinCost: the cheapest one-to-one assignment of rows to columns (Hungarian method). */

/** Potentials, matching and scratch of one run (1-based, index 0 is the free start). */
type State = { n: number; cost: (i: number, j: number) => number; u: Float64Array; v: Float64Array; p: Int32Array; way: Int32Array };

/** An element of a typed array (0 past the end). */
const at = (a: Float64Array | Int32Array, i: number): number => a[i] ?? 0;

/** Lowers `minv` from row `p[j0]` and returns the free column with the least reduced cost and that cost. */
function cheapest(s: State, j0: number, used: Uint8Array, minv: Float64Array): [number, number] {
  const i0 = at(s.p, j0);
  let delta = Infinity;
  let j1 = 0;
  for (let j = 1; j <= s.n; j++) {
    if (used[j] === 1) continue;
    const cur = s.cost(i0 - 1, j - 1) - at(s.u, i0) - at(s.v, j);
    if (cur < at(minv, j)) { minv[j] = cur; s.way[j] = j0; }
    if (at(minv, j) < delta) { delta = at(minv, j); j1 = j; }
  }
  return [j1, delta];
}

/** One step from column `j0`: shifts the potentials by the least reduced cost and returns the column it reaches. */
function relax(s: State, j0: number, used: Uint8Array, minv: Float64Array): number {
  const [j1, delta] = cheapest(s, j0, used, minv);
  for (let j = 0; j <= s.n; j++) {
    if (used[j] !== 1) { minv[j] = at(minv, j) - delta; continue; }
    s.u[at(s.p, j)] = at(s.u, at(s.p, j)) + delta;
    s.v[j] = at(s.v, j) - delta;
  }
  return j1;
}

/** Adds row `i` to the matching along the cheapest augmenting path. */
function addRow(s: State, i: number): void {
  s.p[0] = i;
  let j0 = 0;
  const minv = new Float64Array(s.n + 1).fill(Infinity);
  const used = new Uint8Array(s.n + 1);
  do {
    used[j0] = 1;
    j0 = relax(s, j0, used, minv);
  } while (at(s.p, j0) !== 0);
  do {
    const j1 = at(s.way, j0);
    s.p[j0] = at(s.p, j1);
    j0 = j1;
  } while (j0 !== 0);
}

/** For an `n` x `n` cost, the column assigned to each row (0-based) such that the summed cost is the least. */
export function assignMinCost(n: number, cost: (row: number, col: number) => number): number[] {
  const s: State = { n, cost, u: new Float64Array(n + 1), v: new Float64Array(n + 1), p: new Int32Array(n + 1), way: new Int32Array(n + 1) };
  for (let i = 1; i <= n; i++) addRow(s, i);
  const rowOf = new Array<number>(n).fill(-1);
  for (let j = 1; j <= n; j++) if (at(s.p, j) > 0) rowOf[at(s.p, j) - 1] = j - 1;
  return rowOf;
}
